import { useMemo } from 'react';
import { useCommercialModels } from '@/shared/hooks/useCommercialModels';
import { isGenerativeTaskLoose, isProviderAllowedForTask } from '@/shared/domain/taskTypes';

/**
 * Pure catalog analysis shared by ``useHasNoCommercialProviders`` and
 * ``ModelSelection``. Mirrors the catalog filtering in ``ModelSelection``:
 * cloud providers do not count for generative tasks, and classification-only
 * decision-model providers (TypeSafe AI's Jev) do not count on any other
 * task type.
 *
 * @returns {{
 *   hasNoSelectableModels: boolean,
 *   hasClassificationOnlyProvidersExcluded: boolean,
 * }}
 * ``hasClassificationOnlyProvidersExcluded`` is true when at least one
 * configured provider with models was dropped only because of the
 * classification-only restriction — the case where "no providers" is really
 * "TypeSafe AI supports classification tasks only".
 */
export function analyzeCommercialProviders(availableProviders, taskType) {
    const isCloudDisabled = isGenerativeTaskLoose(taskType);
    let hasSelectableModels = false;
    let hasClassificationOnlyProvidersExcluded = false;

    for (const [provider, info] of Object.entries(availableProviders || {})) {
        const isCloud = info?.provider_type === 'cloud';
        if (isCloudDisabled && isCloud) continue;
        const models = Array.isArray(info?.models) ? info.models : [];
        if (!models.some((m) => m?.id)) continue;
        if (!isProviderAllowedForTask(provider, taskType)) {
            hasClassificationOnlyProvidersExcluded = true;
            continue;
        }
        hasSelectableModels = true;
    }

    return {
        hasNoSelectableModels: !hasSelectableModels,
        hasClassificationOnlyProvidersExcluded,
    };
}

/**
 * Provider-catalog availability for the current task type.
 *
 * ``hasNoProviders`` is true once the catalog has loaded and no commercial
 * model is selectable — the condition under which ModelSelection disables its
 * trigger and the "add an API key" alert should be shown.
 *
 * ``classificationOnlyConfigured`` is true when the only reason nothing is
 * selectable is that the configured provider(s) are classification-only
 * (TypeSafe AI) and this task is not classification — the alert then explains
 * that instead of the generic "no providers" text.
 */
export function useHasNoCommercialProviders(taskType) {
    const { availableProviders, isLoading } = useCommercialModels();

    return useMemo(() => {
        if (isLoading) {
            return { hasNoProviders: false, classificationOnlyConfigured: false };
        }
        const { hasNoSelectableModels, hasClassificationOnlyProvidersExcluded } =
            analyzeCommercialProviders(availableProviders, taskType);
        return {
            hasNoProviders: hasNoSelectableModels,
            classificationOnlyConfigured:
                hasNoSelectableModels && hasClassificationOnlyProvidersExcluded,
        };
    }, [availableProviders, isLoading, taskType]);
}
